import * as XLSX from 'xlsx';

/** Section line shape returned by GET /bookkeeping/income-statement/detail. */
export interface PlExportLine {
  entry_date: string;
  entry_number: string;
  invoice_number: string | null;
  counterparty: string | null;
  account_code: string;
  account_name: string;
  description: string;
  amount: number;
}

export type PlExportSectionKey = 'revenue' | 'cost' | 'operating_expenses' | 'other_income' | 'other_expenses';

export interface PlExportData {
  sections: Record<PlExportSectionKey, PlExportLine[]>;
  period: { start: string; end: string };
  view?: 'booked' | 'posted';
}

/** Statement order — sheet order matches the on-screen formula banner. */
const SECTIONS: { key: PlExportSectionKey; sheet: string; label: string }[] = [
  { key: 'revenue', sheet: 'Revenue', label: 'Revenue' },
  { key: 'cost', sheet: 'Direct Costs', label: 'Direct Costs' },
  { key: 'operating_expenses', sheet: 'Operating Expenses', label: 'Operating Expenses' },
  { key: 'other_income', sheet: 'Other Income', label: 'Other Income' },
  { key: 'other_expenses', sheet: 'Other Expenses', label: 'Other Expenses' },
];

const HEADER = ['Date', 'Voucher No.', 'Invoice No.', 'Payer/Payee', 'Account', 'Description', 'Amount'];

const sum = (lines: PlExportLine[]) =>
  Math.round(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;

/** One section sheet: lines grouped by account (code order, dates ascending),
 *  an account subtotal row per group, and a section total row. Amounts stay
 *  numbers so Excel can sum them. */
function buildSectionSheet(lines: PlExportLine[], sectionLabel: string): XLSX.WorkSheet {
  const byAccount = new Map<string, PlExportLine[]>();
  for (const line of lines) {
    const key = line.account_code;
    if (!byAccount.has(key)) byAccount.set(key, []);
    byAccount.get(key)!.push(line);
  }
  const accountKeys = [...byAccount.keys()].sort();

  const aoa: (string | number)[][] = [HEADER];
  for (const code of accountKeys) {
    const group = [...byAccount.get(code)!].sort((a, b) =>
      a.entry_date === b.entry_date ? a.entry_number.localeCompare(b.entry_number) : a.entry_date.localeCompare(b.entry_date));
    const accountName = group[0]?.account_name || code;
    for (const l of group) {
      aoa.push([
        l.entry_date, l.entry_number, l.invoice_number ?? '', l.counterparty ?? '',
        `${code} ${accountName}`, l.description ?? '', Number(l.amount) || 0,
      ]);
    }
    aoa.push(['Subtotal', '', '', '', `${code} ${accountName}`, '', sum(group)]);
  }
  aoa.push([`Total ${sectionLabel}`, '', '', '', '', '', sum(lines)]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 30 }, { wch: 40 }, { wch: 14 }];
  return ws;
}

/** Summary sheet: the formula blocks (Revenue − Direct Costs = GP − OpEx = OP
 *  + Other Income − Other Expenses = NP), totals computed from the same lines. */
function buildSummarySheet(data: PlExportData): XLSX.WorkSheet {
  const total = (key: PlExportSectionKey) => sum(data.sections[key] || []);
  const revenue = total('revenue');
  const cost = total('cost');
  const grossProfit = revenue - cost;
  const opex = total('operating_expenses');
  const operatingProfit = grossProfit - opex;
  const otherIncome = total('other_income');
  const otherExpenses = total('other_expenses');
  const netProfit = operatingProfit + otherIncome - otherExpenses;

  const viewLabel = data.view === 'booked' ? 'Booked' : 'Posted';
  const aoa: (string | number)[][] = [
    [`Income Statement ${data.period.start} to ${data.period.end} (${viewLabel} view)`],
    [],
    ['Revenue', revenue],
    ['Less: Direct Costs', cost],
    ['Gross Profit', grossProfit],
    ['Less: Operating Expenses', opex],
    ['Operating Profit', operatingProfit],
    ['Add: Other Income', otherIncome],
    ['Less: Other Expenses', otherExpenses],
    ['Net Profit', netProfit],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 30 }, { wch: 14 }];
  return ws;
}

export function buildPlWorkbook(data: PlExportData): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(data), 'Summary');
  for (const section of SECTIONS) {
    XLSX.utils.book_append_sheet(wb, buildSectionSheet(data.sections[section.key] || [], section.label), section.sheet);
  }
  return wb;
}

/** Generate and download the .xlsx for the statement period. */
export function exportPlXlsx(data: PlExportData): void {
  const wb = buildPlWorkbook(data);
  XLSX.writeFile(wb, `income-statement_${data.period.start}_to_${data.period.end}.xlsx`);
}
